import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Trash2, Plus, Send, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HRNotifications() {
  const [user, setUser] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/hr/login'); return; }
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'hr') { navigate('/hr/login'); return; }
    setUser(parsedUser);
    fetchRecipients();
  }, [navigate]);

  const fetchRecipients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notification-settings`, { headers: { Authorization: `Bearer ${token}` } });
      setRecipients(res.data);
    } catch (error) {
      console.error('Error fetching recipients:', error);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { toast.error('Phone number is required'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notification-settings`, { phone_number: phone.trim(), label: label.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Number added successfully');
      setPhone(''); setLabel(''); setShowAddForm(false);
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add number');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this number from notifications?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notification-settings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Number removed');
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleTestMessage = async () => {
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/notification-settings/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const results = res.data.results || [];
      const sent = results.filter(r => r.status === 'sent').length;
      const failed = results.filter(r => r.status === 'failed').length;
      if (sent > 0) toast.success(`Test message sent to test number(s): ${results.filter(r => r.status === 'sent').map(r => r.phone).join(', ')}`);
      if (failed > 0) toast.error(`Failed to send to ${failed} number(s). Check if they joined the Twilio sandbox.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally { setTesting(false); }
  };

  const handleDailySummary = async () => {
    setSendingSummary(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notification-settings/daily-summary`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Daily summary sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send daily summary');
    } finally { setSendingSummary(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/hr/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-white border-b-2 border-zinc-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <img src="/anmol-logo.jpg" alt="Advanced Anmol Metcomp Pvt Ltd" className="h-12" />
            <button onClick={handleLogout} className="text-sm text-zinc-600 hover:text-zinc-900 font-medium" data-testid="notifications-logout-button">Logout</button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-zinc-900" data-testid="notifications-title">WhatsApp Notifications</h1>
              <p className="text-sm text-zinc-500 font-medium mt-1">{user.name}</p>
            </div>
          </div>
          <div className="flex gap-4 mt-6 flex-wrap">
            <button onClick={() => navigate('/hr/dashboard')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-dashboard-from-notifications">Attendance</button>
            <button onClick={() => navigate('/hr/employees')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-employees-from-notifications">Employees</button>
            <button onClick={() => navigate('/hr/locations')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-locations-from-notifications">Locations</button>
            <button onClick={() => navigate('/hr/reports')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-reports-from-notifications">Reports</button>
            <button onClick={() => navigate('/hr/notifications')} className="px-4 py-2 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider rounded-sm" data-testid="nav-notifications-active">Notifications</button>
            <button onClick={() => navigate('/hr/tracking')} className="px-4 py-2 bg-white border-2 border-zinc-200 text-zinc-900 text-sm font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900" data-testid="nav-tracking-from-notifications">Tracking</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Info box */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-sm p-4 mb-6">
          <p className="text-sm font-bold text-blue-900 mb-1">How WhatsApp Notifications Work</p>
          <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
            <li>When an employee marks attendance (check-in or check-out), a WhatsApp message is automatically sent to the numbers below</li>
            <li>A daily attendance summary is sent automatically at <strong>8:00 PM IST</strong> every day</li>
            <li>Each recipient must first join the Twilio WhatsApp sandbox by sending <strong>"join &lt;code&gt;"</strong> to the sandbox number</li>
            <li>Phone numbers must include country code (e.g., +919999831838)</li>
          </ul>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <button onClick={() => setShowAddForm(!showAddForm)} className="h-12 px-6 bg-zinc-900 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-zinc-800 active:scale-95 transition-all flex items-center gap-2" data-testid="add-recipient-button">
            <Plus size={20} /> Add Number
          </button>
          {recipients.length > 0 && (
            <button onClick={handleTestMessage} disabled={testing} className="h-12 px-6 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50" data-testid="test-whatsapp-button">
              <Send size={18} /> {testing ? 'Sending...' : 'Send Test Message (test number only)'}
            </button>
          )}
          {recipients.length > 0 && (
            <button onClick={handleDailySummary} disabled={sendingSummary} className="h-12 px-6 bg-blue-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-blue-700 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50" data-testid="send-daily-summary-button">
              <FileText size={18} /> {sendingSummary ? 'Sending...' : 'Send Daily Summary Now'}
            </button>
          )}
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="bg-white border-2 border-zinc-100 rounded-lg p-6 mb-6">
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-zinc-900 mb-4" data-testid="add-recipient-form-title">Add WhatsApp Number</h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Phone Number (with country code)</label>
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors" placeholder="+919999831838" data-testid="recipient-phone-input" disabled={loading} />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">Label (optional)</label>
                <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} className="w-full h-12 px-4 bg-white border-2 border-zinc-200 rounded-sm text-base font-medium focus:border-zinc-900 focus:outline-none transition-colors" placeholder="e.g., Anmol (Owner)" data-testid="recipient-label-input" disabled={loading} />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="h-12 px-6 bg-green-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-green-700 active:scale-95 transition-all disabled:opacity-50" data-testid="submit-recipient-button" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Number'}
                </button>
                <button type="button" onClick={() => setShowAddForm(false)} className="h-12 px-6 bg-white border-2 border-zinc-200 text-zinc-900 font-bold uppercase tracking-wider rounded-sm hover:border-zinc-900 active:scale-95 transition-all" data-testid="cancel-add-recipient-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Recipients list */}
        <div className="space-y-3">
          {recipients.map((r, idx) => (
            <div key={r.id} className="bg-white border-2 border-zinc-100 rounded-lg p-5 flex items-center justify-between hover:border-zinc-300 transition-colors" data-testid={`recipient-card-${idx}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Bell size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-zinc-900" data-testid={`recipient-label-${idx}`}>{r.label || r.phone_number}</p>
                  <p className="font-mono text-sm text-zinc-500" data-testid={`recipient-phone-${idx}`}>{r.phone_number}</p>
                </div>
              </div>
              <button onClick={() => handleDelete(r.id)} className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors" data-testid={`delete-recipient-${idx}`}>
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>

        {recipients.length === 0 && !showAddForm && (
          <div className="text-center py-12 bg-white border-2 border-zinc-100 rounded-lg">
            <Bell size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500 font-medium">No notification numbers configured</p>
            <p className="text-sm text-zinc-400 mt-1">Add WhatsApp numbers to receive attendance alerts</p>
          </div>
        )}
      </div>
    </div>
  );
}
